import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Table,
  Button,
  Space,
  Tag,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Switch,
  Badge,
  Avatar,
  Tooltip,
  Checkbox,
  Divider,
  Spin,
} from 'antd';
import { useResponsive } from '../hooks/useResponsive';
import {
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserOutlined,
  EnvironmentOutlined,
  SafetyOutlined,
  MailOutlined,
  CalendarOutlined,
  CheckOutlined,
  SaveOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { KeyRound } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import { userService } from '../services/userService';
import { User, UserCreate, UserUpdate, Role, Location } from '../types';
import { RBACService, PERMISSIONS } from '../utils/rbac';
import { apiService } from '../services/apiService';
import { useNavigate, useLocation } from 'react-router-dom';
import ModernFormModal from '../components/ModernFormModal';
import PermissionMultiSelect from '../components/PermissionMultiSelect';
import {
  getPermissionDisplay,
  sortPermissionPickerItems,
  type PermissionPickerItem,
} from '../utils/permissionLabels';
import './Users.css';
import './AdminDashboard.css';

const { Title } = Typography;
const { Option } = Select;
const PASSWORD_RULE_TEXT = 'Use at least 8 chars with uppercase, lowercase, number, and special character.';

function getPasswordScore(pwd: string): number {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8) score += 1;
  if (/[A-Z]/.test(pwd)) score += 1;
  if (/[a-z]/.test(pwd)) score += 1;
  if (/\d/.test(pwd)) score += 1;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
  return score;
}

function getPasswordStrengthLabel(score: number): string {
  if (score <= 1) return 'Weak';
  if (score <= 3) return 'Fair';
  if (score === 4) return 'Good';
  return 'Strong';
}

type PlanType = 'standard' | 'premium' | 'enterprise';

function normalizePlanType(raw: unknown): PlanType {
  const p = String(raw ?? '').trim().toLowerCase();
  if (p === 'enterprise') return 'enterprise';
  if (p === 'premium') return 'premium';
  return 'standard';
}

function parsePlanType(raw: unknown): PlanType | null {
  const p = String(raw ?? '').trim().toLowerCase();
  if (p === 'enterprise') return 'enterprise';
  if (p === 'premium') return 'premium';
  if (p === 'standard' || p === 'basic') return 'standard';
  return null;
}

function extractPlanFromSession(): PlanType {
  try {
    const sessionData = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const candidates = [
      sessionData?.user?.company?.plan_type,
      sessionData?.company?.plan_type,
      sessionData?.user?.companyPlanType,
      sessionData?.user?.company_plan_type,
      sessionData?.user?.plan_type,
      sessionData?.plan_type,
    ];
    const picked = candidates.find((v) => v != null && String(v).trim() !== '');
    const parsed = parsePlanType(picked);
    return parsed ?? 'standard';
  } catch {
    return 'standard';
  }
}

const Users: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const [activeOnly, setActiveOnly] = useState(false); // Default to false to show all users for Admin
  const [searchText, setSearchText] = useState('');
  const [deactivatingId, setDeactivatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [emailStatus, setEmailStatus] = useState<{ email_configured: boolean; message: string } | null>(null);
  const [userPermsLoading, setUserPermsLoading] = useState(false);
  const [formDataLoading, setFormDataLoading] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [resetPasswordTargetUser, setResetPasswordTargetUser] = useState<User | null>(null);
  const [resetPasswordSubmitting, setResetPasswordSubmitting] = useState(false);
  const isAddUserRoute = location.pathname === '/admin-portal/users/add';
  const canCreateUser = RBACService.hasPermission(PERMISSIONS.USER_CREATE);
  const canUpdateUser = RBACService.hasPermission(PERMISSIONS.USER_UPDATE);
  const canDeleteUser = RBACService.hasPermission(PERMISSIONS.USER_DELETE);
  // Prevent browser autofill: inputs start readonly, become editable on first focus
  const [noAutofillFocus, setNoAutofillFocus] = useState({
    username: false,
    email: false,
    createPassword: false,
    confirmPassword: false,
  });

  const createPasswordValue = Form.useWatch('create_password', form) || '';
  const passwordScore = React.useMemo(() => getPasswordScore(String(createPasswordValue)), [createPasswordValue]);
  const passwordStrength = React.useMemo(() => getPasswordStrengthLabel(passwordScore), [passwordScore]);

  const allPermissionOptions = React.useMemo(() => {
    const keys = Object.values(PERMISSIONS) as string[];
    const unique = Array.from(new Set(keys)).sort();
    const moduleOf = (p: string) => (p.split(':')[0] || 'general').toUpperCase();
    const groups: Record<string, { value: string; label: string }[]> = {};
    for (const p of unique) {
      const mod = moduleOf(p);
      if (!groups[mod]) groups[mod] = [];
      groups[mod].push({ value: p, label: p });
    }
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, options]) => ({ label, options }));
  }, []);

  const [companyPlanType, setCompanyPlanType] = useState<PlanType>('standard');
  useEffect(() => {
    let cancelled = false;
    const localPlan = extractPlanFromSession();
    setCompanyPlanType(localPlan);

    // Fallback to /auth/me because session payload shape differs across login flows.
    apiService
      .get<any>('/auth/me')
      .then((me) => {
        if (cancelled) return;
        const remotePlan = parsePlanType(
          me?.company?.plan_type ??
            me?.company?.planType ??
            me?.company_plan_type ??
            me?.companyPlanType ??
            me?.plan_type ??
            me?.planType,
        );
        if (remotePlan) {
          setCompanyPlanType(remotePlan);
        }
      })
      .catch(() => {
        // Keep locally inferred plan when /me is unavailable.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const standardPlanAllowed = React.useMemo(
    () =>
      new Set<string>([
        'dashboard_view',
        'pos_manage',
        'product_create',
        'product_read',
        'product_update',
        'product_delete',
        'category_read',
        'category_create',
        'category_update',
        'category_delete',
        'inventory_read',
        'document_read',
        'document_upload',
        'report_view',
      ]),
    []
  );

  const premiumPlanAllowed = React.useMemo(
    () =>
      new Set<string>([
        ...Array.from(standardPlanAllowed),
        'vendor_create',
        'vendor_read',
        'vendor_update',
        'vendor_delete',
        'vendor_product_manage',
        'inventory_create',
        'inventory_update',
        'inventory_delete',
        'stock_movement_create',
        'stock_adjustment_approve',
        'stock_adjustment_create',
        'stock_transfer_create',
        'stock_transfer_receive',
        'purchase_create',
        'purchase_read',
        'purchase_update',
        'purchase_delete',
        'purchase_send',
        'goods_receive',
        'goods_receive_complete',
        'document_update',
        'report_export',
      ]),
    [standardPlanAllowed]
  );

  const enterprisePlanAllowed = React.useMemo(
    () =>
      new Set<string>([
        ...Array.from(premiumPlanAllowed),
        'locations_view_all',
        'view_analytics',
        'document_delete',
        'edit_content',
        'manage_users',
        'manage_settings',
      ]),
    [premiumPlanAllowed]
  );

  const planAllowedSet = React.useMemo<Set<string> | null>(() => {
    if (companyPlanType === 'enterprise') return enterprisePlanAllowed;
    if (companyPlanType === 'premium') return premiumPlanAllowed;
    return standardPlanAllowed;
  }, [companyPlanType, enterprisePlanAllowed, premiumPlanAllowed, standardPlanAllowed]);

  // Never assign company-admin-only permissions to normal staff through this picker.
  const nonAssignableStaffPermissions = React.useMemo(
    () =>
      new Set<string>([
        // Sales CRUD is not used in frontend staff portal (reports come from POS/report permissions).
        'sales_create',
        'sales_read',
        'sales_update',
        'sales_delete',
        'user_create',
        'user_read',
        'user_update',
        'user_delete',
        'location_create',
        'location_read',
        'location_update',
        'location_delete',
        'role_create',
        'role_read',
        'role_update',
        'role_delete',
        'manage_users',
        'manage_settings',
      ]),
    []
  );

  const permissionOptionsForRoleWithPlan = React.useMemo(() => {
    return allPermissionOptions
      .map((grp) => ({
        ...grp,
        options: grp.options.filter((o) => {
          if (nonAssignableStaffPermissions.has(o.value)) return false;
          if (!planAllowedSet) return true;
          return planAllowedSet.has(o.value);
        }),
      }))
      .filter((grp) => grp.options.length > 0);
  }, [allPermissionOptions, nonAssignableStaffPermissions, planAllowedSet]);

  const permissionPickerItems = React.useMemo((): PermissionPickerItem[] => {
    const items: PermissionPickerItem[] = [];
    for (const grp of permissionOptionsForRoleWithPlan) {
      for (const o of grp.options) {
        items.push({
          code: o.value,
          ...getPermissionDisplay(o.value),
        });
      }
    }
    return sortPermissionPickerItems(items);
  }, [permissionOptionsForRoleWithPlan]);

  // Check access — company admin portal or user_read permission
  useEffect(() => {
    const canViewUsers =
      RBACService.isCompanyAdmin() || RBACService.hasPermission(PERMISSIONS.USER_READ);

    if (!canViewUsers) {
      message.error('Access denied. You do not have permission to view users.');
      navigate('/dashboard');
    }
  }, [navigate]);

  // When Add User modal opens, fetch email config status and reset no-autofill focus
  useEffect(() => {
    if (modalVisible && !editingUser) {
      setEmailStatus(null);
      setNoAutofillFocus({ username: false, email: false, createPassword: false, confirmPassword: false });
      userService
        .getEmailStatus()
        .then((res) => setEmailStatus(res))
        .catch(() => setEmailStatus({ email_configured: false, message: 'Could not check email configuration.' }));
    }
  }, [modalVisible, editingUser]);

  // Fetch data from backend
  const fetchUsers = useCallback(async () => {
    const canViewUsers =
      RBACService.isCompanyAdmin() || RBACService.hasPermission(PERMISSIONS.USER_READ);

    if (!canViewUsers) {
      message.error('Access denied. You do not have permission to view users.');
      setUsers([]);
      return;
    }
    
    setLoading(true);
    try {
      const currentUser = RBACService.getCurrentUser();
      const data = await userService.getUsers({
        active_only: activeOnly,
      });
      // Hide the currently logged-in admin from the list (backend also enforces this)
      const currentUserId = currentUser?.id ? Number.parseInt(currentUser.id, 10) : null;
      const filtered = Number.isFinite(currentUserId as number)
        ? data.filter((u) => u.id !== (currentUserId as number))
        : data;
      setUsers(filtered);
      console.log(`Fetched ${data.length} users (active_only: ${activeOnly})`);
    } catch (error: any) {
      // Handle specific error cases
      if (error?.response?.status === 403) {
        message.error('Access denied. Only Admin users can view users.');
      } else if (error?.response?.status === 401) {
        message.error('Session expired. Please login again.');
      } else {
      message.error('Failed to fetch users');
      }
      console.error('Error fetching users:', error);
      setUsers([]); // Clear users on error
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  const fetchRoles = useCallback(async () => {
    try {
      const data = await userService.getRoles();
      setRoles(data);
    } catch (error) {
      message.error('Failed to fetch roles');
      console.error('Error fetching roles:', error);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const data = await userService.getLocations({
        active_only: true,
      }, { forceRefresh: true });
      setLocations(data);
    } catch (error) {
      message.error('Failed to fetch locations');
      console.error('Error fetching locations:', error);
    }
  }, []);

  // When Add User modal opens, only re-fetch if data not already loaded
  useEffect(() => {
    if (!modalVisible) return;
    // If locations already loaded, no need to show loading spinner
    if (locations.length > 0) {
      setFormDataLoading(false);
      return;
    }
    let cancelled = false;
    setFormDataLoading(true);
    Promise.all([fetchLocations(), fetchRoles().catch(() => undefined)])
      .then(() => { if (!cancelled) setFormDataLoading(false); })
      .catch(() => { if (!cancelled) setFormDataLoading(false); });
    return () => { cancelled = true; };
  }, [modalVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fetch locations on page load so modal opens instantly.
  useEffect(() => {
    fetchUsers();
    fetchLocations();
    fetchRoles().catch(() => undefined);
  }, [fetchUsers, fetchLocations, fetchRoles]);

  // Route-driven add-user screen:
  // - /admin-portal/users/add => show add form only
  // - /admin-portal/users => show user list/details
  useEffect(() => {
    if (isAddUserRoute) {
      handleCreate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddUserRoute]);

  // If user leaves /users/add, make sure add form is closed.
  useEffect(() => {
    if (!isAddUserRoute && modalVisible && !editingUser) {
      setModalVisible(false);
      form.resetFields();
    }
  }, [isAddUserRoute, modalVisible, editingUser, form]);

  // Close Add User modal when user clicks "User Management" in the sidebar
  useEffect(() => {
    const handleCloseModal = () => {
      setModalVisible(false);
      setEditingUser(null);
      form.resetFields();
    };
    window.addEventListener('admin-close-add-user-modal', handleCloseModal);
    return () => window.removeEventListener('admin-close-add-user-modal', handleCloseModal);
  }, [form]);

  // Filter users based on search
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      searchText === '' ||
      user.username.toLowerCase().includes(searchText.toLowerCase()) ||
      user.email.toLowerCase().includes(searchText.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchText.toLowerCase());
    return matchesSearch;
  });

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = async (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role_id: user.role_id,
      location_id: user.location_id,
      is_active: user.is_active,
    });
    setUserPermsLoading(true);
    try {
      const perms = await userService.getUserPermissions(user.id);
      form.setFieldsValue({
        permission_grants: perms.grants || [],
      });
    } catch {
      form.setFieldsValue({
        permission_grants: [],
      });
    } finally {
      setUserPermsLoading(false);
    }
    setModalVisible(true);
  };

  const sanitizeAssignableGrants = useCallback(
    (raw: string[]): string[] => {
      const unique = Array.from(new Set(raw || []));
      return unique.filter((p) => {
        if (nonAssignableStaffPermissions.has(p)) return false;
        if (!planAllowedSet) return true;
        return planAllowedSet.has(p);
      });
    },
    [nonAssignableStaffPermissions, planAllowedSet]
  );

  const handleSubmit = async () => {
    if (submitting) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingUser) {
        // Update existing user
        const updateData: UserUpdate = {
          username: values.username,
          email: values.email,
          first_name: values.first_name,
          last_name: values.last_name,
          role_id: values.role_id,
          location_id: values.location_id,
          is_active: values.is_active,
        };
        
        await userService.updateUser(editingUser.id, updateData);
        const grants = sanitizeAssignableGrants((values.permission_grants || []) as string[]);
        await userService.updateUserPermissions(editingUser.id, { grants, denies: [] });
        message.success('✅ User updated successfully');
      } else {
        // Create new user
        const createData: UserCreate = {
          username: values.username,
          email: values.email,
          first_name: values.first_name,
          last_name: values.last_name,
          location_id: values.location_id,
          password: values.create_password,
          is_active: values.is_active ?? true,
        };
        
        const notifyUser = form.getFieldValue('notify_user') !== false;
        const result = await userService.createUser(createData, { notify_user: notifyUser });
        const grants = sanitizeAssignableGrants((values.permission_grants || []) as string[]);
        if (grants.length) {
          try {
            await userService.updateUserPermissions(result.user.id, { grants, denies: [] });
          } catch (permErr) {
            console.warn('Permissions update failed (non-critical):', permErr);
          }
        }
        message.success('✅ User created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingUser(null);
      if (!editingUser && isAddUserRoute) {
        navigate('/admin-portal/users', { replace: true });
      }
      fetchUsers();
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Please fill in all required fields');
        return; // don't close modal or refresh — form has validation errors
      }
      // Network errors can occur even when the server successfully saved the user
      // (e.g. Render worker recycling mid-response). Refresh the list so the user
      // can see whether the record was actually created.
      const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');
      if (isNetworkError && !editingUser) {
        message.warning('⚠️ Network error — but the user may have been created. Refreshing list to check...');
        setModalVisible(false);
        form.resetFields();
        setEditingUser(null);
        fetchUsers();
        return;
      }
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : (editingUser ? 'Failed to update user' : 'Failed to create user'));
      console.error('Error submitting user:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (userId: number) => {
    try {
      setDeactivatingId(userId);
      await userService.deactivateUser(userId);
      message.success('User deactivated successfully');
      await fetchUsers();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Failed to deactivate user';
      message.error(typeof msg === 'string' ? msg : 'Failed to deactivate user');
      console.error('Error deactivating user:', error);
    } finally {
      setDeactivatingId(null);
    }
  };

  // Admin portal: ensure the reset button is visible for Admin users even if
  // the user's effective permission set is missing `user_update` in some cases.
  const resetCanShow = RBACService.isCompanyAdmin() || RBACService.hasPermission(PERMISSIONS.USER_UPDATE);
  const currentUserId = RBACService.getCurrentUser()?.id;

  const openResetPassword = (u: User) => {
    setResetPasswordTargetUser(u);
    setResetPasswordSubmitting(false);
    resetPasswordForm.resetFields();
    setResetPasswordModalVisible(true);
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetPasswordTargetUser) return;
    try {
      setResetPasswordSubmitting(true);
      const response = await userService.resetUserPassword(resetPasswordTargetUser.id);
      message.success(response?.message || 'Password reset link sent');
      setResetPasswordModalVisible(false);
      setResetPasswordTargetUser(null);
      resetPasswordForm.resetFields();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || 'Failed to reset password');
    } finally {
      setResetPasswordSubmitting(false);
    }
  };

  const handleDeletePermanent = async (userId: number) => {
    try {
      setDeletingId(userId);
      await userService.deleteUserPermanent(userId);
      message.success('User deleted permanently');
      await fetchUsers();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Failed to delete user';
      message.error(typeof msg === 'string' ? msg : 'Failed to delete user');
      console.error('Error deleting user:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.first_name} {record.last_name}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              @{record.username}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => (
        <Space>
          <MailOutlined />
          {email}
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      render: (role: any) => (
        <Tag color="blue" icon={<SafetyOutlined />}>
          {role?.name || 'No Role'}
        </Tag>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 150,
      render: (location: any) => (
        <Space>
          <EnvironmentOutlined />
          <span>{location?.name || 'No Location'}</span>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (is_active: boolean) => (
        <Badge
          status={is_active ? 'success' : 'error'}
          text={is_active ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          {new Date(date).toLocaleDateString()}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 176,
      fixed: 'right',
      render: (_, record) => (
        <Space wrap={false} size={8} className="users-action-btns">
          {canUpdateUser && (
            <Tooltip title="Edit" placement="top" getPopupContainer={() => document.body} mouseEnterDelay={0.3}>
              <Button
                aria-label="Edit"
                className="icon-btn icon-btn--primary"
                shape="round"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          {resetCanShow &&
            currentUserId != null &&
            record.id !== Number(currentUserId) &&
            record.role?.name !== 'Super Admin' && (
            <Tooltip title="Reset password" placement="top" getPopupContainer={() => document.body} mouseEnterDelay={0.3}>
              <Button
                aria-label="Reset password"
                className="icon-btn icon-btn--default"
                shape="round"
                icon={<KeyRound size={16} strokeWidth={2.4} />}
                loading={resetPasswordTargetUser?.id === record.id && resetPasswordSubmitting}
                onClick={() => openResetPassword(record)}
              />
            </Tooltip>
          )}
          {canDeleteUser && record.is_active ? (
            <Popconfirm
              title="Delete this user permanently? This cannot be undone."
              onConfirm={() => handleDeletePermanent(record.id)}
              okText="Yes, delete permanently"
              cancelText="Cancel"
              getPopupContainer={() => document.body}
              okButtonProps={{ loading: deletingId === record.id, danger: true }}
            >
              <Tooltip title="Delete" placement="top" getPopupContainer={() => document.body} mouseEnterDelay={0.3}>
                <Button
                  aria-label="Delete permanently (from active user list)"
                  className="icon-btn icon-btn--danger"
                  shape="round"
                  icon={<DeleteOutlined />}
                  loading={deletingId === record.id}
                />
              </Tooltip>
            </Popconfirm>
          ) : canDeleteUser ? (
            <Popconfirm
              title="Delete this user permanently? This cannot be undone."
              onConfirm={() => handleDeletePermanent(record.id)}
              okText="Yes, delete permanently"
              cancelText="Cancel"
              getPopupContainer={() => document.body}
              okButtonProps={{ loading: deletingId === record.id, danger: true }}
            >
              <Tooltip title="Delete" placement="top" getPopupContainer={() => document.body} mouseEnterDelay={0.3}>
                <Button
                  aria-label="Delete permanently"
                  className="icon-btn icon-btn--danger"
                  shape="round"
                  icon={<DeleteOutlined />}
                  loading={deletingId === record.id}
                />
              </Tooltip>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  // Statistics
  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.is_active).length;
  const totalRoles = new Set(users.map((u) => u.role_id)).size;

  return (
    <div className="users-page" style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {!isAddUserRoute && (
        <>
          {/* Header */}
          <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Title level={2} style={{ margin: 0 }}>
                <TeamOutlined /> User Management
              </Title>
            </Col>
            <Col xs={24} md={12}>
              <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Input
                  placeholder="🔍 Search users..."
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: isMobile ? '100%' : 200, maxWidth: isMobile ? '100%' : 200, minWidth: isMobile ? undefined : 200, flex: 'none' }}
                />
                <Switch
                  checked={activeOnly}
                  onChange={setActiveOnly}
                  checkedChildren="Active"
                  unCheckedChildren="All"
                />
                <Button icon={<ReloadOutlined />} onClick={fetchUsers}>
                  Refresh
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Statistics Cards */}
          <Row gutter={[16, 16]} className="ad-cards-row" style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={8}>
              <Card hoverable className="ad-stat-card ad-stat-card--lavender" onClick={() => setActiveOnly(false)}>
                <div className="ad-stat-card__head">
                  <span className="ad-stat-card__title">TOTAL USERS</span>
                  <div className="ad-stat-card__iconWrap" aria-hidden="true">
                    <TeamOutlined />
                  </div>
                </div>
                <div className="ad-stat-card__value">{totalUsers}</div>
                <div className="ad-stat-card__meta">All registered users</div>
                <div className="ad-stat-card__bottom">
                  <span className="ad-stat-card__trend">View all</span>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card hoverable className="ad-stat-card ad-stat-card--green" onClick={() => setActiveOnly(true)}>
                <div className="ad-stat-card__head">
                  <span className="ad-stat-card__title">ACTIVE USERS</span>
                  <div className="ad-stat-card__iconWrap" aria-hidden="true">
                    <UserOutlined />
                  </div>
                </div>
                <div className="ad-stat-card__value">{activeUsers}</div>
                <div className="ad-stat-card__meta">{totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0}% of total users</div>
                <div className="ad-stat-card__bottom">
                  <span className="ad-stat-card__trend">Filter active</span>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card hoverable className="ad-stat-card ad-stat-card--blue" onClick={() => { setActiveOnly(false); setSearchText(''); }}>
                <div className="ad-stat-card__head">
                  <span className="ad-stat-card__title">ROLES USED</span>
                  <div className="ad-stat-card__iconWrap" aria-hidden="true">
                    <SafetyOutlined />
                  </div>
                </div>
                <div className="ad-stat-card__value">{totalRoles}</div>
                <div className="ad-stat-card__meta">Distinct roles assigned</div>
                <div className="ad-stat-card__bottom">
                  <span className="ad-stat-card__trend">Show all</span>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Table Card - grows to fill remaining viewport */}
          <div className="users-table-wrapper" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Card 
              className="users-table-card"
              bodyStyle={{ 
                overflowX: 'auto', 
                overflowY: 'hidden',
                padding: 0,
                position: 'relative',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
              }}
              style={{ 
                position: 'relative',
                zIndex: 1,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div className="users-table-inner" style={{ position: 'relative', zIndex: 1, flex: 1, minHeight: 0 }}>
                <Table
                  columns={columns}
                  dataSource={filteredUsers}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    defaultPageSize: 20,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} users`,
                    responsive: true
                  }}
                  scroll={{ 
                    x: 1200,
                    y: isMobile ? 'max(220px, calc(100dvh - 420px))' : 'max(280px, calc(100dvh - 400px))'
                  }}
                  size={isMobile ? 'small' : 'middle'}
                />
              </div>
            </Card>
          </div>
        </>
      )}

      {/* Add/Edit Modal - same look as Purchase Order form (light blue banner, title, subtitle, footer) */}
      <ModernFormModal
        open={modalVisible}
        onClose={() => {
          if (submitting) return;
          setModalVisible(false);
          setEditingUser(null);
          form.resetFields();
          if (!editingUser && isAddUserRoute) {
            navigate('/admin-portal/users', { replace: true });
          }
        }}
        title={editingUser ? 'Edit User' : 'Add New User'}
        subtitle={editingUser ? 'Update the user details below.' : 'Fill in the details below to add a new user.'}
        headerVariant="light"
        getContainer={() => document.querySelector('.main-content-full') as HTMLElement || document.body}
        wrapClassName="form-panel-full-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        cancelText="Cancel"
        okText={editingUser ? 'Update User' : 'Create User'}
        okIcon={<SaveOutlined />}
        onOk={() => form.submit()}
        isSubmitting={submitting || formDataLoading}
        okDisabled={formDataLoading}
        destroyOnClose
      >
        {formDataLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 40px' }}>
            <Spin size="large" />
            <Typography.Title level={4} style={{ marginTop: 16 }}>Loading form data...</Typography.Title>
            <Typography.Text type="secondary">
              Fetching roles and locations. If this takes a while, the server may be waking up — please wait.
            </Typography.Text>
          </div>
        ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="add-user-form"
          initialValues={{
            is_active: true,
            notify_user: true,
          }}
          autoComplete="off"
          style={{ width: '100%' }}
        >
            {/* Hidden fake inputs so browser autofill targets these instead of real fields */}
            <div style={{ position: 'absolute', left: '-9999px', top: 0, width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
              <input type="text" name="username" tabIndex={-1} autoComplete="username" readOnly aria-hidden="true" />
              <input type="password" name="password" tabIndex={-1} autoComplete="current-password" readOnly aria-hidden="true" />
              <input type="password" name="new-password" tabIndex={-1} autoComplete="new-password" readOnly aria-hidden="true" />
            </div>

            <Divider orientation="left">User details</Divider>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="first_name"
                  label="First Name"
                  rules={[{ required: true, message: 'Please enter first name' }]}
                >
                  <Input placeholder="e.g., John" size="large" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="last_name"
                  label="Last Name"
                  rules={[{ required: true, message: 'Please enter last name' }]}
                >
                  <Input placeholder="e.g., Doe" size="large" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="username"
                  label="Username"
                  rules={[{ required: true, message: 'Please enter username' }]}
                >
                  <Input
                    placeholder="e.g., johndoe"
                    size="large"
                    autoComplete="off"
                    name="new_user_username_field"
                    readOnly={!noAutofillFocus.username}
                    onFocus={() => setNoAutofillFocus((prev) => ({ ...prev, username: true }))}
                    data-lpignore="true"
                    data-form-type="other"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Please enter email' },
                    { type: 'email', message: 'Enter a valid email' },
                  ]}
                >
                  <Input
                    placeholder="e.g., john@company.com"
                    size="large"
                    autoComplete="off"
                    name="new_user_email_field"
                    readOnly={!noAutofillFocus.email}
                    onFocus={() => setNoAutofillFocus((prev) => ({ ...prev, email: true }))}
                    data-lpignore="true"
                    data-form-type="other"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                </Form.Item>
              </Col>
            </Row>

            {!editingUser && (
              <>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="create_password"
                      label="Create Password"
                      rules={[
                        { required: true, message: 'Please enter create password' },
                        { min: 8, message: 'Password must be at least 8 characters' },
                        {
                          validator: async (_, value) => {
                            const v = String(value || '');
                            if (!v) return Promise.resolve();
                            if (!/[A-Z]/.test(v)) return Promise.reject(new Error('Include at least one uppercase letter'));
                            if (!/[a-z]/.test(v)) return Promise.reject(new Error('Include at least one lowercase letter'));
                            if (!/\d/.test(v)) return Promise.reject(new Error('Include at least one number'));
                            if (!/[^A-Za-z0-9]/.test(v)) return Promise.reject(new Error('Include at least one special character'));
                            return Promise.resolve();
                          },
                        },
                      ]}
                      extra={PASSWORD_RULE_TEXT}
                    >
                      <Input.Password
                        placeholder="Create password"
                        size="large"
                        autoComplete="one-time-code"
                        name="create_user_password_field"
                        readOnly={!noAutofillFocus.createPassword}
                        onFocus={() => setNoAutofillFocus((prev) => ({ ...prev, createPassword: true }))}
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-bwignore="true"
                        data-form-type="other"
                      />
                    </Form.Item>

                    <div className="password-strength-wrap" aria-label={`Password strength: ${passwordStrength}`}>
                      <div className="password-strength-bars" role="presentation">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <span
                            key={n}
                            className={`password-strength-bar ${n <= passwordScore ? 'is-active' : ''} ${
                              passwordScore <= 1
                                ? 'level-weak'
                                : passwordScore <= 3
                                  ? 'level-fair'
                                  : passwordScore === 4
                                    ? 'level-good'
                                    : 'level-strong'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="password-strength-text">{createPasswordValue ? passwordStrength : 'Password strength'}</span>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="confirm_password"
                      label="Confirm Password"
                      dependencies={['create_password']}
                      rules={[
                        { required: true, message: 'Please confirm password' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('create_password') === value) return Promise.resolve();
                            return Promise.reject(new Error('The two passwords do not match'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        placeholder="Confirm password"
                        size="large"
                        autoComplete="one-time-code"
                        name="create_user_password_confirm_field"
                        readOnly={!noAutofillFocus.confirmPassword}
                        onFocus={() => setNoAutofillFocus((prev) => ({ ...prev, confirmPassword: true }))}
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-bwignore="true"
                        data-form-type="other"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            <Divider orientation="left">Location</Divider>
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="location_id"
                  label="Location"
                  rules={[{ required: true, message: 'Please select location' }]}
                >
                  <Select
                    placeholder="Select location"
                    size="large"
                    showSearch
                    optionFilterProp="children"
                  >
                    {locations.map((location) => (
                      <Option key={location.id} value={location.id}>
                        {location.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item name="permission_grants" label="Permissions">
                  <PermissionMultiSelect
                    options={permissionPickerItems}
                    loading={userPermsLoading}
                    placeholder="Select permissions for this user"
                  />
                </Form.Item>
              </Col>
              {/* Deny permissions hidden (not used for now) */}
            </Row>

            <Divider orientation="left">Options</Divider>
            {!editingUser && (
              <>
                <Form.Item
                  name="notify_user"
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Checkbox>Send welcome email to this user with their account details (username, email, location)</Checkbox>
                </Form.Item>
                {emailStatus && (
                  <div style={{ marginTop: -8, marginBottom: 8 }}>
                    <Typography.Text type={emailStatus.email_configured ? 'success' : 'secondary'} style={{ fontSize: 12 }}>
                      {emailStatus.email_configured ? '✓ ' : ''}{emailStatus.message}
                    </Typography.Text>
                  </div>
                )}
              </>
            )}

            {editingUser && (
              <Form.Item
                name="is_active"
                label="Status"
                valuePropName="checked"
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            )}
        </Form>
        )}
      </ModernFormModal>

      {/* Reset Password Modal */}
      <ModernFormModal
        open={resetPasswordModalVisible}
        onClose={() => {
          if (resetPasswordSubmitting) return;
          setResetPasswordModalVisible(false);
          setResetPasswordTargetUser(null);
          resetPasswordForm.resetFields();
        }}
        title="Send Password Reset Link"
        subtitle={resetPasswordTargetUser ? `Set a new password for ${resetPasswordTargetUser.first_name} ${resetPasswordTargetUser.last_name}` : 'Set a new password'}
        headerVariant="light"
        getContainer={() => document.querySelector('.main-content-full') as HTMLElement || document.body}
        wrapClassName="form-panel-full-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        cancelText="Cancel"
        okText="Send Reset Link"
        okIcon={<KeyOutlined />}
        onOk={() => resetPasswordForm.submit()}
        isSubmitting={resetPasswordSubmitting}
        okDisabled={false}
        destroyOnClose
      >
        <Form
          form={resetPasswordForm}
          layout="vertical"
          onFinish={handleResetPasswordSubmit}
          className="add-user-form"
          autoComplete="off"
          style={{ width: '100%' }}
        >
          <div style={{ position: 'absolute', left: '-9999px', top: 0, width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
            <input type="text" name="username" tabIndex={-1} autoComplete="username" readOnly aria-hidden="true" />
            <input type="password" name="password" tabIndex={-1} autoComplete="current-password" readOnly aria-hidden="true" />
            <input type="password" name="new-password" tabIndex={-1} autoComplete="new-password" readOnly aria-hidden="true" />
          </div>
          <Divider orientation="left">Email reset</Divider>
          <p style={{ marginBottom: 0 }}>
            A secure password reset link will be sent to{' '}
            <strong>{resetPasswordTargetUser?.email || 'this user'}</strong>. The user will choose their own
            password from the link, so the Admin does not see or email a plaintext password.
          </p>
        </Form>
      </ModernFormModal>
    </div>
  );
};

export default Users;